import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Compass,
  Shield,
  TrendingUp,
  Send,
  Heart
} from 'lucide-react';
import Navigation from '../components/Navigation';

const GetDisciplerPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    goals: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Discipler request:', formData);
  };

  const benefits = [
    {
      icon: Compass,
      title: 'Guidance',
      description: 'Navigate life decisions with wisdom from someone who has walked the path'
    },
    {
      icon: Shield,
      title: 'Accountability',
      description: 'Stay committed to your spiritual growth with consistent support'
    },
    {
      icon: TrendingUp,
      title: 'Growth',
      description: 'Accelerate your faith journey through intentional mentorship'
    }
  ];

  const matchingSteps = [
    { number: '1', title: 'Apply', description: 'Complete your discipleship interest form' },
    { number: '2', title: 'Get Matched', description: 'We pair you with a mentor suited to your needs' },
    { number: '3', title: 'Begin Journey', description: 'Start meeting regularly and growing together' }
  ];

  const testimonials = [
    {
      quote: "My discipler helped me see God's purpose for my life when I felt lost. The accountability and encouragement changed everything.",
      author: 'Marcus T.',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop'
    },
    {
      quote: "Having someone walk with me through doubts and questions made my faith real and personal. I'm forever grateful.",
      author: 'Sarah K.',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero */}
      <div className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1920&h=1080&fit=crop')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B]/90 via-[#0B1B3B]/80 to-[#0B1B3B]/70" />
        
        <div className="relative z-10 text-center text-white px-4 max-w-5xl">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Get a <span className="text-[#CFA64B]">Discipler</span>
          </h1>
          <p className="text-xl md:text-2xl mb-10 text-gray-200">
            Grow stronger in faith with a mentor who walks with you.
          </p>
          <Button className="bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold px-10 py-6 text-lg">
            Request a Discipler
          </Button>
        </div>
      </div>

      {/* Why Discipleship Matters */}
      <div className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Why Discipleship Matters</h2>
            <p className="text-xl text-gray-600">Faith grows best when we walk together</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((item, idx) => {
              const Icon = item.icon;
              return (
                <Card key={idx} className="border-2 border-gray-200 hover:border-[#CFA64B] hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-8 text-center">
                    <div className="w-20 h-20 mx-auto mb-6 bg-[#CFA64B]/10 rounded-full flex items-center justify-center">
                      <Icon className="w-10 h-10 text-[#CFA64B]" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0B1B3B] mb-4">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* How We Match You */}
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">How We Match You</h2>
            <p className="text-xl text-gray-600">A simple process for meaningful connection</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {matchingSteps.map((step, idx) => (
              <Card key={idx} className="border-2 border-gray-200">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-6 bg-[#CFA64B] text-white rounded-full flex items-center justify-center text-3xl font-bold">
                    {step.number}
                  </div>
                  <h3 className="text-2xl font-bold text-[#0B1B3B] mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Success Stories */}
      <div className="py-20 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Success Stories</h2>
            <p className="text-xl text-gray-600">Hear from those who've experienced transformation</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, idx) => (
              <Card key={idx} className="border-2 border-gray-200">
                <CardContent className="p-8">
                  <div className="flex items-start gap-4 mb-6">
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.author}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-bold text-[#0B1B3B] text-lg">{testimonial.author}</p>
                      <p className="text-sm text-gray-600">Discipleship Participant</p>
                    </div>
                  </div>
                  <p className="text-gray-700 italic leading-relaxed">"{testimonial.quote}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Sign-Up Form */}
      <div className="py-20" style={{ backgroundColor: '#FFF9E9' }}>
        <div className="max-w-[800px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Request a Discipler</h2>
            <p className="text-xl text-gray-600">Take your first step toward guided growth</p>
          </div>

          <Card className="border-none shadow-xl">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Name *</label>
                  <input type="text" required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none" placeholder="Your full name" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Email *</label>
                  <input type="email" required value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none" placeholder="your@email.com" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Goals</label>
                  <input type="text" value={formData.goals} onChange={(e) => setFormData({...formData, goals: e.target.value})} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none" placeholder="What do you hope to achieve?" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Message</label>
                  <textarea value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} rows={5} className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none resize-none" placeholder="Tell us more about yourself..." />
                </div>
                <Button type="submit" className="w-full bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold py-6 text-lg">
                  <Send className="w-5 h-5 mr-2" />
                  Submit Request
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* CTA */}
      <div className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B] to-[#CFA64B]" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <Heart className="w-16 h-16 mx-auto mb-6 text-[#CFA64B]" />
          <h2 className="text-4xl md:text-6xl font-bold mb-10">Faith grows best in community.</h2>
          <Button className="bg-white text-[#0B1B3B] hover:bg-gray-100 font-bold px-10 py-6 text-lg">Join Now</Button>
        </div>
      </div>

      {/* Quick Links */}
      <div className="py-6 bg-gray-50 border-t border-gray-200">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <a href="/attendee-guide" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">Attendee Guide</a>
            <span className="text-gray-400">|</span>
            <a href="/faqs" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">FAQs</a>
            <span className="text-gray-400">|</span>
            <a href="#contact" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">Contact Support</a>
          </div>
        </div>
      </div>

      <footer className="bg-[#0f172a] text-white py-12">
        <div className="max-w-[1440px] mx-auto px-6 text-center">
          <p className="text-gray-400 text-sm">© 2025 KNOWN CON. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default GetDisciplerPage;